import { h } from 'preact';
import { useDispatch, useSelector } from 'react-redux';

import { FeaturesJrRakuPack } from '@travel/icons/features';
import { ArrowLeft, Clock } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import MealOption from '@travel/traveler-core/components/ItemFeatures/MealOption';
import PaymentMethod from '@travel/traveler-core/components/ItemFeatures/PaymentMethod';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { IconText } from '@travel/ui';

import ImageGallery from 'components/ImageGallery';

import { pushLocation } from 'store/__router/actions';

import paths from 'constants/paths';

import { getQueryString } from 'store/__router/selectors';

import styles from './planInfo.module.scss';

import type { HasMeal, MediaType } from 'Common-Types';
type Props = {
  hotelId: string;
  media: MediaType[];
  name: string;
  description: string;
  hasMeal: HasMeal;
  checkInTime: string;
  lastCheckInTime: string;
};

const PlanInfo = (props: Props) => {
  const { media, name, hasMeal, description, checkInTime, lastCheckInTime, hotelId } = props;

  const { isPC } = useDeviceTypes();
  const dispatch = useDispatch();
  const search = useSelector(getQueryString);

  const goToPlanList = () =>
    dispatch(pushLocation(`${paths.plansList.pathResolver(hotelId)}${search}`));

  const checkInTimeText = [checkInTime, lastCheckInTime].filter(info => info).join('~');

  return (
    <div className={styles.container}>
      <div className={styles.titleHeader}>
        <IconText
          className={styles.planListLink}
          text={<Translate id="Plan_List.Detail.Back_To_List" />}
          icon={<ArrowLeft size={16} className={styles.arrowLeft} />}
          spaceBetween={4}
          width={8}
          textClassName={styles.linkText}
          height={16}
          onClick={goToPlanList}
          data-testid="PlanInfo-backPlanList"
        />
        <FeaturesJrRakuPack className={styles.jrRakuPack} />
      </div>
      {!isPC && (
        <ImageGallery
          media={media}
          galleryType="slide"
          mediaLimit={3}
          swiperClassName={styles.swiper}
          className={styles.imageGallery}
          isTitle
          mediaTitleClass={styles.mediaSlideTitle}
          galleryClassName={styles.imageWrapper}
        />
      )}
      <h2 className={styles.planName}>{name}</h2>
      <div className={styles.splitContent}>
        <div>
          <div className={styles.planFeatures}>
            <MealOption hasMeal={hasMeal} className={styles.HasMeal} iconSize={20} />
            <span className={styles.divider} />
            <PaymentMethod size={20} />
            {checkInTimeText && (
              <>
                <span className={styles.divider} />
                <IconText
                  className={styles.trainTime}
                  textClassName={styles.trainText}
                  icon={<Clock size={20} />}
                  spaceBetween={4}
                  text={
                    <>
                      <Translate
                        id="DP_Common.Search_Condition.Check_In"
                        className={styles.spacing}
                      />
                      {checkInTimeText}
                    </>
                  }
                />
              </>
            )}
          </div>
          {description && <p className={styles.description}>{description}</p>}
          <Translate className={styles.noBedNotice} id="Plan_List.Detail.Infant_No_Bed.Notice" />
        </div>
        {isPC && (
          <ImageGallery
            media={media}
            galleryType="thumbnail"
            mediaLimit={3}
            swiperClassName={styles.imageGalleryPC}
            className={styles.imageGalleryPC}
            galleryClassName={styles.imageWrapper}
            thumbnailClass={styles.galleryThumbnail}
            mediaTitleClass={styles.mediaTitle}
            isTitle
          />
        )}
      </div>
    </div>
  );
};

export default PlanInfo;
